<script setup lang="tsx">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { getMethodStatusText } from '@/services/utils'
import { NotImplemented } from '@/models/enums-generated'
import type {
	MethodImportMatchedMethod as MatchedMethod,
	MethodImportUnmatchedMethod as UnmatchedMethod,
	MethodImportNameMismatch as NameMismatch,
	MethodImportSuggestedItem as SuggestedItem,
} from '@/models/api-generated'
import { statusIcon, importResponsibleIcon, fmtDate, effectiveStatus as _effectiveStatus } from './importUtils'

const props = defineProps<{
	confirmedMatchedGroups: Array<{ key: string; module: string; moduleTitle: string; assetName: string }>
	confirmedMatchedByGroup: Record<string, MatchedMethod[]>
	plannedActivationsCount: number
	plannedByGroup: Record<string, UnmatchedMethod[]>
	uniquePlannedModules: Array<UnmatchedMethod & { groupKey: string }>
	skippedUnmatched: UnmatchedMethod[]
	matchedSize: number
	expandedConfirmMatched: Set<string>
	expandedConfirmPlanned: Set<string>
	expandedModuleIds: Set<number>
	mismatchByName: Record<string, NameMismatch>
	statusReplacementMap: Record<string, string>
	moduleAssets: Record<string, SuggestedItem>
	moduleBps: Record<string, SuggestedItem[]>
}>()

const { t } = useI18n()

const effectiveStatus = (s: string) => _effectiveStatus(s, props.statusReplacementMap)

const allStep3Expanded = computed(() => {
	const total = props.confirmedMatchedGroups.length + props.uniquePlannedModules.length
	return total > 0 && props.expandedConfirmMatched.size + props.expandedConfirmPlanned.size === total
})

function toggle<T>(set: Set<T>, key: T) {
	if (set.has(key)) set.delete(key)
	else set.add(key)
}

function remainingModuleMethods(mod: UnmatchedMethod & { groupKey: string }) {
	const planned = props.plannedByGroup[mod.groupKey] ?? []
	return mod.allModuleMethods.filter((mm) => !planned.some((m) => m.name === mm.name))
}

function toggleAll() {
	if (allStep3Expanded.value) {
		props.expandedConfirmMatched.clear()
		props.expandedConfirmPlanned.clear()
	} else {
		for (const g of props.confirmedMatchedGroups) props.expandedConfirmMatched.add(g.key)
		for (const m of props.uniquePlannedModules) props.expandedConfirmPlanned.add(m.groupKey)
	}
}
</script>

<template>
	<div class="alert alert-warning py-2 small mb-3">
		<i class="bi bi-exclamation-circle me-1"></i>{{ t('massEdit.warning') }}
	</div>

	<div v-if="confirmedMatchedGroups.length || plannedActivationsCount" class="d-flex justify-content-end mb-2">
		<button type="button" class="btn btn-link btn-sm p-0 text-muted" @click="toggleAll">
			<i :class="allStep3Expanded ? 'bi bi-chevron-double-up' : 'bi bi-chevron-double-down'" class="me-1"></i>{{ allStep3Expanded ? t('import.collapseAll') : t('import.expandAll') }}
		</button>
	</div>

	<template v-if="confirmedMatchedGroups.length">
		<h6 class="mb-2">{{ t('method.label', 2) }} ({{ matchedSize }})</h6>
		<div v-for="grp in confirmedMatchedGroups" :key="grp.key" class="card mb-2">
				<div
					class="card-header d-flex align-items-center gap-2 cursor-pointer"
					@click="toggle(expandedConfirmMatched, grp.key)"
				>
					<div class="d-flex align-items-center gap-1">
						<i class="bi bi-layers text-muted"></i>
						<span class="fw-semibold">{{ grp.module }}</span>
						<span v-if="grp.moduleTitle" class="text-muted small">— {{ grp.moduleTitle }}</span>
					</div>
					<span v-if="grp.assetName" class="text-muted small ms-2">
						<i class="bi bi-clipboard me-1"></i>{{ grp.assetName }}
					</span>
					<span class="badge bg-secondary ms-auto">{{ confirmedMatchedByGroup[grp.key]?.length ?? 0 }}</span>
					<i :class="expandedConfirmMatched.has(grp.key) ? 'bi-chevron-up' : 'bi-chevron-down'" class="bi ms-1 text-muted"></i>
				</div>
				<div v-if="expandedConfirmMatched.has(grp.key)" class="card-body py-2">
					<div
						v-for="m in confirmedMatchedByGroup[grp.key]"
						:key="m.metaId"
						class="d-flex align-items-baseline flex-wrap gap-2 py-1 border-bottom"
					>
						<div class="flex-grow-1">
							<span class="fw-semibold small">{{ m.name }}</span>
							<span v-if="m.methodTitle" class="text-muted small ms-1">— {{ m.methodTitle }}</span>
							<span v-if="m.highSecNeedWarning" class="badge bg-warning text-dark ms-1 fw-normal small">
								<i class="bi bi-exclamation-triangle me-1"></i>{{ t('method.securityType') }}
							</span>
							<span
								v-if="mismatchByName[m.name]"
								class="badge bg-warning text-dark ms-1 fw-normal small"
								:title="mismatchByName[m.name].inFile + ' → ' + mismatchByName[m.name].inDB"
							>
								<i class="bi bi-file-earmark-diff me-1"></i>{{ t('import.versionMismatch') }}
							</span>
						</div>
						<span class="small text-muted">
							<i :class="statusIcon(effectiveStatus(m.imported.status) || NotImplemented)" class="me-1"></i>{{ getMethodStatusText(effectiveStatus(m.imported.status) || NotImplemented) }}
						</span>
						<span class="small text-muted">
							<i v-if="m.imported.responsibleId" :class="`bi ${importResponsibleIcon(m.imported.responsibleType)} me-1`"></i>{{ m.imported.responsibleId ? m.imported.responsibleLabel : '—' }}
						</span>
						<span class="small text-muted">
							<i class="bi bi-calendar me-1"></i>{{ fmtDate(m.imported.dueDate) }}
						</span>
					</div>
				</div>
		</div>
	</template>

	<template v-if="plannedActivationsCount">
		<h6 class="mb-2">{{ t('misc.import') }} ({{ plannedActivationsCount }})</h6>
		<div v-for="mod in uniquePlannedModules" :key="mod.groupKey" class="card mb-3">
				<div
					class="card-header d-flex align-items-center gap-2 cursor-pointer"
					@click="toggle(expandedConfirmPlanned, mod.groupKey)"
				>
					<div class="d-flex align-items-center gap-1 flex-grow-1 flex-wrap">
						<i class="bi bi-layers text-muted"></i>
						<span class="fw-semibold">{{ mod.module }}</span>
						<span v-if="mod.moduleTitle" class="text-muted small">— {{ mod.moduleTitle }}</span>
						<span v-if="moduleAssets[mod.groupKey]?.name" class="text-muted small ms-2">
							<i class="bi bi-clipboard me-1"></i>{{ moduleAssets[mod.groupKey].name }}
						</span>
						<span v-for="bp in (moduleBps[mod.groupKey] ?? [])" :key="bp.id" class="text-muted small">
							<i class="bi bi-kanban me-1"></i>{{ bp.name }}
						</span>
					</div>
					<span class="badge bg-info text-dark ms-auto flex-shrink-0">
						<i class="bi bi-lightning-charge-fill me-1"></i>{{ t('import.moduleWillBeActivated') }}
					</span>
					<i :class="expandedConfirmPlanned.has(mod.groupKey) ? 'bi-chevron-up' : 'bi-chevron-down'" class="bi ms-1 text-muted flex-shrink-0"></i>
				</div>
				<div v-if="expandedConfirmPlanned.has(mod.groupKey)" class="card-body py-2">
					<div
						v-for="m in plannedByGroup[mod.groupKey]"
						:key="m.methodId"
						class="d-flex align-items-baseline gap-2 py-1 border-bottom"
					>
						<i class="bi bi-check-circle-fill text-success small flex-shrink-0"></i>
						<div class="flex-grow-1">
							<span class="fw-semibold small">{{ m.name }}</span>
							<span v-if="m.highSecNeedWarning" class="badge bg-warning text-dark ms-1 fw-normal small">
								<i class="bi bi-exclamation-triangle me-1"></i>{{ t('method.securityType') }}
							</span>
							<span
								v-if="mismatchByName[m.name]"
								class="badge bg-warning text-dark ms-1 fw-normal small"
								:title="mismatchByName[m.name].inFile + ' → ' + mismatchByName[m.name].inDB"
							>
								<i class="bi bi-file-earmark-diff me-1"></i>{{ t('import.versionMismatch') }}
							</span>
							<span v-if="m.methodTitle" class="text-muted small ms-1">— {{ m.methodTitle }}</span>
						</div>
						<span class="small text-muted">
							<i :class="statusIcon(effectiveStatus(m.imported.status) || NotImplemented)" class="me-1"></i>{{ getMethodStatusText(effectiveStatus(m.imported.status) || NotImplemented) }}
						</span>
						<span class="small text-muted">
							<i v-if="m.imported.responsibleId" :class="`bi ${importResponsibleIcon(m.imported.responsibleType)} me-1`"></i>{{ m.imported.responsibleId ? m.imported.responsibleLabel : '—' }}
						</span>
						<span v-if="m.imported.dueDate" class="small text-muted">
							<i class="bi bi-calendar me-1"></i>{{ fmtDate(m.imported.dueDate) }}
						</span>
					</div>
					<button
						v-if="mod.allModuleMethods?.length > (plannedByGroup[mod.groupKey]?.length ?? 0)"
						type="button"
						class="btn btn-link btn-sm p-0 mt-2 text-muted"
						@click="toggle(expandedModuleIds, mod.moduleId)"
					>
						<i :class="expandedModuleIds.has(mod.moduleId) ? 'bi bi-chevron-up' : 'bi bi-chevron-down'" class="me-1"></i>
						<template v-if="expandedModuleIds.has(mod.moduleId)">{{ t('misc.close') }}</template>
						<template v-else>+{{ mod.allModuleMethods.length - (plannedByGroup[mod.groupKey]?.length ?? 0) }} {{ t('method.label', 2).toLowerCase() }}</template>
					</button>
					<template v-if="expandedModuleIds.has(mod.moduleId)">
						<div
							v-for="mm in remainingModuleMethods(mod)"
							:key="mm.name"
							class="d-flex align-items-baseline gap-2 py-1 border-bottom text-muted"
						>
							<i class="bi bi-circle small flex-shrink-0"></i>
							<div>
								<span class="small">{{ mm.name }}</span>
								<span v-if="mm.description" class="text-muted small ms-1">— {{ mm.description }}</span>
							</div>
						</div>
					</template>
				</div>
			</div>
	</template>

	<div v-if="skippedUnmatched.length > 0" class="alert alert-warning py-2 small mb-2 d-flex align-items-center gap-2">
		<i class="bi bi-exclamation-triangle-fill flex-shrink-0"></i>
		<span>{{ skippedUnmatched.length }} {{ t('method.label', skippedUnmatched.length).toLowerCase() }} — {{ t('asset.label') }}: {{ t('misc.nothing') }}</span>
	</div>

	<div v-if="!matchedSize && !plannedActivationsCount" class="alert alert-info">
		{{ t('misc.nothing') }}
	</div>
</template>

